import { logAnalyticSFSendStockouts } from 'features/order/substitution-flow/infra';
import { substitutionFlowAdapter } from '../substitutionFlow.adapter';
import { substitutionFlowRepository } from '../substitutionFlow.repository';

import type {
  BaseReplacementWithPartnerData,
  GetItemsReplacementParams,
  GetSuggestionsWithAIParams,
  ItemsReplacementModel,
  RemoveItemParams,
  SetReplaceMultipleItemsParams,
  SetReplaceSingleItemParams,
} from 'features/order/substitution-flow/domain';
import type { OrderDetailModel } from 'shared/domain';
import type { FlowTypeToEvent } from '../analytics';

export function getPartnerId() {
  return substitutionFlowRepository.readPartnerIdFromProfile();
}

export async function getItemsReplacement(params: GetItemsReplacementParams) {
  const paramsToRepo = substitutionFlowAdapter.toReadItemsReplacement(params);
  return substitutionFlowRepository
    .readItemsReplacement(paramsToRepo)
    .then(r => substitutionFlowAdapter.toItemsReplacement(r.data));
}

export async function getSuggestionsWithAI(params: GetSuggestionsWithAIParams) {
  const paramsToRepo = substitutionFlowAdapter.toReadSuggestionsWithAI(params);
  return substitutionFlowRepository
    .readSuggestionsWithAI(paramsToRepo)
    .then(r => substitutionFlowAdapter.toSuggestionsWithAI(r.data));
}

export async function setReplaceSingleItem(params: SetReplaceSingleItemParams) {
  const partnerId = getPartnerId();
  const { fromChatBtn, suggestionsAreFromAI, ...restParams } = params;
  const paramsToRepo = substitutionFlowAdapter.toReplaceSingleItem({
    ...restParams,
    partnerId,
  });
  return substitutionFlowRepository
    .substituteSingleItem(paramsToRepo)
    .then(_ => {
      startSupportAndAnalytic({
        ...params,
        partnerId,
        flow: 'substitution',
        fromChatBtn,
        suggestionsAreFromAI,
      });
      return true;
    });
}

export async function setReplaceMultipleItems(
  params: SetReplaceMultipleItemsParams,
) {
  const partnerId = getPartnerId();
  const { fromChatBtn, ...restParams } = params;
  const paramsToRepo = substitutionFlowAdapter.toReplaceMultipleItems({
    ...restParams,
    partnerId,
  });
  return substitutionFlowRepository
    .substituteMultipleItems(paramsToRepo)
    .then(_ => {
      startSupportAndAnalytic({
        ...params,
        partnerId,
        flow: 'stockout',
        fromChatBtn,
      });
      return true;
    });
}

export async function setReplaceSingleItemWithoutSuggestions(
  params: SetReplaceMultipleItemsParams,
) {
  const partnerId = getPartnerId();
  const { fromChatBtn, ...restParams } = params;
  const paramsToRepo = substitutionFlowAdapter.toReplaceMultipleItems({
    ...restParams,
    partnerId,
  });
  paramsToRepo.payload.flow_type = 'DIRECT_USER_CONTACT_SUBSTITUTION_FLOW';
  return substitutionFlowRepository
    .substituteMultipleItems(paramsToRepo)
    .then(_ => {
      startSupportAndAnalytic({
        ...params,
        partnerId,
        flow: 'stockout',
        fromChatBtn,
      });
      return true;
    });
}

export function setCancelOrderInSF(order: OrderDetailModel) {
  const products = order.substitutionFlowState?.products ?? [];
  const customizations = order.substitutionFlowState?.toppings ?? [];
  return substitutionFlowRepository.updateCancelOrder({
    orderId: order.id,
    payload: {
      stockout_products: products.map(p => p.productId) ?? [],
      stockout_toppings: customizations.map(t => t.id) ?? [],
    },
  });
}

export async function removeItemFromOrder(
  params: RemoveItemParams,
): Promise<boolean> {
  const payload = substitutionFlowAdapter.toRemoveItemPayload(params);
  const response = await substitutionFlowRepository.removeItem({
    orderId: params.orderId,
    payload,
  });
  return response.data;
}

interface StartSupportAndAnalyticParams extends BaseReplacementWithPartnerData {
  flow: FlowTypeToEvent;
  fromChatBtn: boolean;
  suggestions?: ItemsReplacementModel;
  suggestionsAreFromAI?: boolean[];
}
function startSupportAndAnalytic(params: StartSupportAndAnalyticParams) {
  const { flow, fromChatBtn, ...restParams } = params;
  const initialModification = substitutionFlowAdapter.toReplaceSingleItem({
    suggestions: { products: [], toppings: [] },
    ...restParams,
  }).payload.initial_modification;
  substitutionFlowRepository.sendSubstitutionEventToChatSupport({
    detail: {
      ...initialModification,
      is_stockout_v2: flow === 'stockout',
      is_new_substitution_flow: flow === 'substitution',
    },
  });
  logAnalyticSFSendStockouts({ ...params });
}
